import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { Points } from 'components/Points/Points';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import AdminTradeExpandedRow from 'screens/Admin/Trades/components/AdminTradeExpandedRow';
import type { AdminTrade } from 'store/services/trade/tradeApi';
import type { FeedTrade, FeedTradeStatus } from 'types/interfaces/feed';
import './FeedListView.css';

const STATUS_COLOR: Record<FeedTradeStatus, string> = {
  ready: '#3b82f6',
  transit: '#f59e0b',
  completed: '#10b981',
};

const STATUS_LABEL: Record<FeedTradeStatus, string> = {
  ready: 'Ready to Ship',
  transit: 'In Transit',
  completed: 'Completed',
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}.${dd}.${d.getFullYear()}`;
};

const toAdminTrade = (t: FeedTrade): AdminTrade => ({
  id: t.id,
  tradeNumber: t.tradeNumber,
  senderId: t.senderId,
  senderUsername: t.senderUsername,
  senderAvatar: t.senderAvatar ?? undefined,
  receiverId: t.receiverId,
  receiverUsername: t.receiverUsername,
  receiverAvatar: t.receiverAvatar ?? undefined,
  totalValue: t.totalValue,
  initiatedAt: t.initiatedAt,
  shippedAt: t.shippedAt ?? undefined,
  completedAt: t.completedAt ?? undefined,
  status: t.status,
  items: t.items,
});

interface FeedListViewProps {
  trades: FeedTrade[];
}

export const FeedListView: React.FC<FeedListViewProps> = ({ trades }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const goToProfile = (username: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/profile/u/${username}`);
  };

  return (
    <div className="feed-list-view trades-table-container">
      <table className="trades-table">
        <thead>
          <tr>
            <th style={{ width: 40 }}></th>
            <th>Trade #</th>
            <th>Sender</th>
            <th>Value</th>
            <th>Receiver</th>
            <th>Initiated At</th>
            <th>Shipped At</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((trade) => {
            const isExpanded = expanded.has(trade.id);
            return (
              <React.Fragment key={trade.id}>
                <tr
                  className={`trades-row-clickable ${isExpanded ? 'trades-row-expanded' : ''}`}
                  onClick={() => toggle(trade.id)}
                >
                  <td className="trades-chevron-cell">
                    <span className={`trades-chevron ${isExpanded ? 'trades-chevron-open' : ''}`}>
                      &#9654;
                    </span>
                  </td>
                  <td className="trades-trade-number">{trade.tradeNumber}</td>
                  <td>
                    <div
                      className="trades-user feed-list-view__user"
                      onClick={goToProfile(trade.senderUsername)}
                    >
                      <AvatarWithFlag
                        avatarUrl={trade.senderAvatar ?? undefined}
                        username={trade.senderUsername}
                        countryCode={trade.senderCountryCode ?? undefined}
                        size={32}
                      />
                      <span className="trades-username">{trade.senderUsername}</span>
                    </div>
                  </td>
                  <td>
                    <Points points={trade.totalValue} type="small" />
                  </td>
                  <td>
                    <div
                      className="trades-user feed-list-view__user"
                      onClick={goToProfile(trade.receiverUsername)}
                    >
                      <AvatarWithFlag
                        avatarUrl={trade.receiverAvatar ?? undefined}
                        username={trade.receiverUsername}
                        countryCode={trade.receiverCountryCode ?? undefined}
                        size={32}
                      />
                      <span className="trades-username">{trade.receiverUsername}</span>
                    </div>
                  </td>
                  <td className="trades-date">{formatDate(trade.initiatedAt)}</td>
                  <td className="trades-date">{formatDate(trade.shippedAt)}</td>
                  <td>
                    <span
                      className="trades-status-badge"
                      style={{
                        backgroundColor: STATUS_COLOR[trade.status],
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#fff',
                      }}
                    >
                      {STATUS_LABEL[trade.status]}
                    </span>
                  </td>
                </tr>
                {isExpanded && (
                  <AdminTradeExpandedRow
                    trade={toAdminTrade(trade)}
                    readOnly
                    colSpan={8}
                  />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default FeedListView;
